import { LoginLayout } from "@app/components/layouts/LoginLayout";
import { LoginByCafe24 } from "@app/pages/auth/platform/cafe24/Login";
import { SignupByCafe24 } from "@app/pages/auth/platform/cafe24/Signup";
import { PaymentManager } from "@app/pages/billling/Admin";
import { PaymentMethods } from "@app/pages/billling/Cards";
import { PaymentHistory } from "@app/pages/billling/History";
import { LoginByApp } from "@app/pages/webview/Login";
import { FindPassword } from "@app/pages/auth/FindPassword";
import { Guide } from "@app/pages/Guide";
import { Signup } from "src/pages/auth/Signup";
import { AuthLayout, MainLayout } from "src/components/layouts";
import {
  createBrowserRouter,
  generatePath,
  Link as OrigLink,
  Path as PathType,
  RouteObject, Navigate,
} from "react-router-dom"
import { Dashboard } from "src/pages/Dashboard";
import { Login } from "@app/pages/auth/Login";
import { Editor } from "@app/pages/Editor";
import { Integration } from "@app/pages/widget/Integration";
import { Settings } from "@app/pages/widget/Settings";
import { AccountSettings } from "@app/pages/settings/Account";
import { ShopSettings } from "@app/pages/settings/Shop";
import { Studio } from "@app/pages/widget/Studio";
import { Path } from "@app/route_path";
import { Postings } from "@app/pages/Postings";
import { PostingGroups } from "@app/pages/PostingGroups";

/**
 * Absolute route paths of the app
 */

const mainRoutes: RouteObject[] = [
  {
    path: "*",
    element: <Navigate to={Path.LOGIN} replace />
  },
  {
    element: <LoginLayout />,
    children: [
      {
        path: Path.LOGIN,
        element: <Login />,
      },
      {
        path: Path.FIND_PASSWORD,
        element: <FindPassword />,
      },
      {
        path: Path.SIGNUP,
        element: <Signup />,
        children: [
          {
            path: ":path",
            element: <Signup />,
          },
        ],
      },
    ],
  },
  {
    path: Path.LOGIN_CAFE24,
    element: <LoginByCafe24 />,
  },
  {
    path: Path.SIGNUP_CAFE24,
    element: <SignupByCafe24 />,
  },
  {
    path: Path.LOGIN_APP,
    element: <LoginByApp />,
  },
  {
    path: Path.INDEX,
    element: <AuthLayout />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            index: true,
            element: <Dashboard />,
          },
          {
            path: Path.WIDGET_INTEGRATION,
            element: <Integration />,
          },
          {
            path: Path.WIDGET_SETTINGS,
            element: <Settings />,
          },
          {
            path: Path.WIDGET_EMBED,
            element: <Studio />,
          },
          {
            path: Path.EDITOR,
            element: <Editor />,
          },
          {
            path: Path.POSTINGS,
            element: <Postings />,
          },
          {
            path: Path.POSTING_GROUPS,
            element: <PostingGroups />,
          },
          {
            path: Path.ACCOUNT_SETTINGS,
            element: <AccountSettings />,
          },
          {
            path: Path.SHOP_SETTINGS,
            element: <ShopSettings />,
          },
          {
            path: Path.PAYMENT_METHOD,
            element: <PaymentMethods />,
          },
          {
            path: Path.PAYMENT_ADMIN,
            element: <PaymentManager />,
          },
          {
            path: Path.PAYMENT_HISTORY,
            element: <PaymentHistory />,
          },
          {
            path: Path.GUIDE,
            element: <Guide />,
          },
        ],
      },
    ],
  },
];

const mainRouter = createBrowserRouter(mainRoutes);

type PathObject<T extends string> = Parameters<typeof generatePath<T>>[1];
type PathWithParams<T extends Path> = Partial<Omit<PathType, "pathname">> & {
  path: T;
  params?: PathObject<T>;
};

export interface LinkProps<T extends Path>
  extends Omit<React.ComponentProps<typeof OrigLink>, "to"> {
  path: T;
  search?: string;
  hash?: string;
  params?: PathObject<T>;
}

/**
 * generatePath 함수를 자동화하는 Link 컴포넌트
 */
export const Link = <T extends Path>(props: LinkProps<T>) => {
  const { path, search, hash, params, ...rest } = props;
  return (
    <OrigLink
      to={{
        pathname: generatePath(path, params),
        search,
        hash,
      }}
      {...rest}
    />
  );
};

/**
 *  generatePath 함수를 자동화하는 navigate 함수
 */
export const navigate = <T extends Path>(
  to: PathWithParams<T>,
  options?: Parameters<typeof mainRouter.navigate>[1],
) => {
  const { path, params, search, hash } = to;
  return mainRouter.navigate(
    {
      pathname: generatePath(path, params),
      search,
      hash,
    },
    options,
  );
};

export { mainRouter };
